import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Container,
  useTheme,
  useMediaQuery,
  Alert,
  Paper,
} from "@mui/material";
import { Visibility, VisibilityOff, Security, Lock } from "@mui/icons-material";
import { motion } from "framer-motion";
import AuthContext from "../AuthContext";
import { login } from "../apis/axios";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    if (!username.trim() || !password.trim()) {
      setError("Please enter both username and password");
      setIsLoading(false);
      return;
    }

    try {
      const response = await login(username, password);
      const user = response.data.user;
      if (user) auth.signin(user, () => navigate("/"));
    } catch (err) {
      setError(err.response?.data?.message || "Login failed. Check credentials.");
    } finally {
      setIsLoading(false);
    }
  };

  const FloatingShape = ({ delay, size, top, left, right, bottom }) => (
    <motion.div
      style={{
        position: "absolute",
        width: size,
        height: size,
        top,
        left,
        right,
        bottom,
        background: theme.palette.mode === "light"
          ? theme.palette.primary.light + "33"
          : theme.palette.secondary.light + "33",
        borderRadius: "50%",
        filter: "blur(20px)",
      }}
      animate={{ y: [0, -20, 0], x: [0, 15, 0] }}
      transition={{ duration: 6, repeat: Infinity, delay, ease: "easeInOut" }}
    />
  );

  return (
    <Container
      disableGutters
      maxWidth={false}
      sx={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        background: theme.palette.background.default,
        p: isMobile ? 2 : 4,
      }}
    >
      <Grid
        container
        sx={{
          width: "100%",
          maxWidth: 1200,
          height: isMobile ? "auto" : "90vh",
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: theme.shadows[4],
        }}
      >
        {/* Left - Glassmorphic Form */}
        <Grid
          item
          size={{xs:12,md:6}}
          sx={{
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backdropFilter: "blur(25px)",
            backgroundColor:
              theme.palette.mode === "light"
                ? "rgba(255,255,255,0.75)"
                : "rgba(0,0,0,0.65)",
            p: isMobile ? 4 : 6,
          }}
        >
          <Box sx={{ position: "absolute", inset: 0, overflow: "hidden" }}>
            <FloatingShape delay={0} size={140} top="10%" left="-5%" />
            <FloatingShape delay={1} size={100} bottom="10%" right="-5%" />
            <FloatingShape delay={2} size={80} top="70%" left="20%" />
          </Box>

          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            sx={{ width: "100%", maxWidth: 400, zIndex: 2 }}
          >
            {/* Header */}
            <Box sx={{ textAlign: "center", mb: 5 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  background: theme.palette.primary.main,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                  boxShadow: "0 8px 30px rgba(45,91,255,0.3)",
                }}
              >
                <Lock sx={{ color: "white", fontSize: 36 }} />
              </Box>
              <Typography variant="h4" fontWeight={700} color="primary" gutterBottom>
                Secure Access
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Access your devices safely.
              </Typography>
            </Box>

            {/* Form */}
            <Box component="form" onSubmit={handleLogin}>
              <TextField
                label="Username"
                fullWidth
                variant="outlined"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 3,
                    backgroundColor:
                      theme.palette.mode === "light"
                        ? "rgba(255,255,255,0.85)"
                        : "rgba(0,0,0,0.55)",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Security sx={{ color: theme.palette.text.secondary }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Password"
                type={showPassword ? "text" : "password"}
                fullWidth
                variant="outlined"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 3,
                    backgroundColor:
                      theme.palette.mode === "light"
                        ? "rgba(255,255,255,0.85)"
                        : "rgba(0,0,0,0.55)",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ color: theme.palette.text.secondary }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {error && (
                <Alert severity="error" sx={{ borderRadius: 3, mb: 2, fontSize: "0.9rem" }}>
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={{
                  borderRadius: 25,
                  py: 1.2, // Reduced padding
                  fontWeight: 600,
                  fontSize: "1rem",
                  textTransform: "none",
                  boxShadow: theme.shadows[4],
                  minWidth: 120, // Added to ensure text fits
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: theme.shadows[6],
                  },
                  "& .MuiButton-label": {
                    color: theme.palette.secondary.contrastText, // Ensure text is white
                  },
                }}
              >
                {isLoading ? "Authenticating..." : "Login"}
              </Button>
            </Box>

            <Typography
              variant="caption"
              display="block"
              textAlign="center"
              mt={3}
              color="text.secondary"
            >
              🔒 All data is encrypted with enterprise-grade security
            </Typography>
          </Box>
        </Grid>

        {/* Right - Animated Illustration */}
        <Grid
          item
          size={{md:6}}
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            justifyContent: "center",
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
            position: "relative",
            overflow: "hidden",
            p: 4,
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            style={{ textAlign: "center", color: "white" }}
          >
            <Typography variant="h4" fontWeight={700} mb={2}>
              Enterprise Disk Encryption
            </Typography>
            <Typography
              variant="h6"
              sx={{ opacity: 0.9, maxWidth: 400, mx: "auto", mb: 4, lineHeight: 1.6 }}
            >
              Protect sensitive data with zero-compromise encryption & advanced authentication.
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center", gap: 2, flexWrap: "wrap" }}>
              {["AES-256", "FIPS 140-2", "Zero Trust"].map((feature) => (
                <Box
                  key={feature}
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.15)",
                    backdropFilter: "blur(12px)",
                    borderRadius: 3,
                    px: 3,
                    py: 1,
                    border: "1px solid rgba(255,255,255,0.3)",
                    fontWeight: 600,
                  }}
                >
                  {feature}
                </Box>
              ))}
            </Box>
          </motion.div>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Login;